import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, object=authed
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("cvolve_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem("cvolve_token");
        setUser(false);
      })
      .finally(() => setReady(true));
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("cvolve_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.require_otp) {
        return { ok: true, require_otp: true, email: payload.email, message: data.message };
      }
      if (data.token && data.user) {
        localStorage.setItem("cvolve_token", data.token);
        setUser(data.user);
      }
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const verifyOtp = useCallback(async (email, otp) => {
    try {
      const { data } = await api.post("/auth/verify-otp", { email, otp });
      localStorage.setItem("cvolve_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const resendOtp = useCallback(async (email) => {
    try {
      const { data } = await api.post("/auth/resend-otp", { email });
      return { ok: true, message: data.message };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const updateProfile = useCallback(async (payload) => {
    const { data } = await api.patch("/profile", payload);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("cvolve_token");
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, ready, login, register, verifyOtp, resendOtp, logout, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
